<script setup lang="ts">
import type { VEmptyStateKind } from "./VEmptyState.types";
import { computed } from "vue";
import "../../illustrations/empty/emptyScenes.css";

defineOptions({
  name: "VEmptyIllustration",
});

/**
 * The illustration of an empty, no results, no access or error state (illustration-spec): a 200x140 scene on a soft
 * ground with a badge top right. Pass the scene of the screen as SVG elements in the default slot; without one the
 * generic list scene is drawn. The state only changes the badge and how faded the scene is.
 */
const { state = "empty" } = defineProps<{
  state?: VEmptyStateKind
}>();

defineSlots<{
  /** The scene: SVG elements in the 200x140 canvas, standing on the ground at y 124. */
  default?: () => unknown
}>();

const SCENE_OPACITY: Record<VEmptyStateKind, number | undefined> = {
  "empty": undefined,
  "no-results": 0.55,
  "no-access": 0.45,
  "error": 0.45,
};

const sceneOpacity = computed(() => SCENE_OPACITY[state]);
</script>

<template>
  <svg
    width="200"
    height="140"
    viewBox="0 0 200 140"
    aria-hidden="true"
    focusable="false"
    class="block shrink-0"
    :data-state="state"
  >
    <g class="empty-scene" :opacity="sceneOpacity">
      <ellipse cx="100" cy="124" rx="78" ry="9" fill="#F5F5F5" />
      <slot>
        <!-- Generic scene: a list card with a header bar and rows, one row ticked in green. -->
        <rect
          x="40"
          y="30"
          width="120"
          height="90"
          rx="6"
          fill="#FFFFFF"
          stroke="#BDBDBD"
          stroke-width="1.6"
          stroke-linejoin="round"
        />
        <path d="M40 46H160" stroke="#E0E0E0" stroke-width="1.4" />
        <rect
          x="50"
          y="36"
          width="34"
          height="4"
          rx="2"
          fill="#E0E0E0"
        />
        <rect
          x="128"
          y="36"
          width="22"
          height="4"
          rx="2"
          fill="#E0E0E0"
        />
        <rect
          x="50"
          y="55"
          width="10"
          height="10"
          rx="2.5"
          fill="#4CAF50"
        />
        <path
          d="M52.4 60.2l2.2 2.2 4-4.4"
          fill="none"
          stroke="#FFFFFF"
          stroke-width="1.6"
          stroke-linecap="round"
          stroke-linejoin="round"
        />
        <rect
          x="66"
          y="58"
          width="44"
          height="4"
          rx="2"
          fill="#C8E6C9"
        />
        <rect
          x="128"
          y="58"
          width="22"
          height="4"
          rx="2"
          fill="#C8E6C9"
        />
        <path d="M40 74H160" stroke="#E0E0E0" stroke-width="1.4" />
        <rect
          x="50"
          y="81"
          width="10"
          height="10"
          rx="2.5"
          fill="#FFFFFF"
          stroke="#BDBDBD"
          stroke-width="1.6"
          stroke-linejoin="round"
        />
        <rect
          x="66"
          y="84"
          width="36"
          height="4"
          rx="2"
          fill="#E0E0E0"
        />
        <rect
          x="128"
          y="84"
          width="18"
          height="4"
          rx="2"
          fill="#E0E0E0"
        />
        <path d="M40 98H160" stroke="#E0E0E0" stroke-width="1.4" />
        <rect
          x="50"
          y="104"
          width="10"
          height="10"
          rx="2.5"
          fill="#FFFFFF"
          stroke="#BDBDBD"
          stroke-width="1.6"
          stroke-linejoin="round"
        />
        <rect
          x="66"
          y="107"
          width="40"
          height="4"
          rx="2"
          fill="#E0E0E0"
        />
        <rect
          x="128"
          y="107"
          width="20"
          height="4"
          rx="2"
          fill="#E0E0E0"
        />
      </slot>
    </g>
    <g v-if="state === 'empty'" data-badge="empty">
      <circle cx="172" cy="26" r="13" fill="#4CAF50" />
      <path d="M172 20v12M166 26h12" stroke="#FFFFFF" stroke-width="2.2" stroke-linecap="round" />
    </g>
    <g v-else-if="state === 'no-results'" data-badge="no-results">
      <circle
        cx="172"
        cy="26"
        r="13"
        fill="#FFFFFF"
        stroke="#9E9E9E"
        stroke-width="1.6"
      />
      <circle
        cx="170"
        cy="24"
        r="5.2"
        fill="none"
        stroke="#757575"
        stroke-width="1.8"
      />
      <path d="M173.8 27.8l4 4" stroke="#757575" stroke-width="2" stroke-linecap="round" />
      <path d="M168.3 22.3l3.4 3.4M171.7 22.3l-3.4 3.4" stroke="#9E9E9E" stroke-width="1.4" stroke-linecap="round" />
    </g>
    <g v-else-if="state === 'no-access'" data-badge="no-access">
      <circle cx="172" cy="26" r="13" fill="#FFF6D3" />
      <path d="M168.2 25v-3.2a3.8 3.8 0 0 1 7.6 0V25" fill="none" stroke="#A14E0B" stroke-width="1.8" stroke-linecap="round" />
      <rect
        x="165.5"
        y="24.5"
        width="13"
        height="9.5"
        rx="2"
        fill="#A14E0B"
      />
      <circle cx="172" cy="28.6" r="1.4" fill="#FFF6D3" />
    </g>
    <g v-else data-badge="error">
      <circle cx="172" cy="26" r="13" fill="#FFEBEE" />
      <path d="M172 19.5v8.5" stroke="#C62828" stroke-width="2.6" stroke-linecap="round" />
      <circle cx="172" cy="32.6" r="1.6" fill="#C62828" />
    </g>
  </svg>
</template>
